
import { Component, Vue, Watch } from 'vue-property-decorator';
import WebsiteMigrationsRepositoryInterface, { WebsiteMigration } from '@/services/WebsiteMigrations/interfaces';
import { DataTableHeader } from 'vuetify';
import WebsiteRepositoryInterface from '@/services/Website/interfaces';
import { ServerRepositoryInterface } from '@/services/Server/Interfaces';
import WebsiteGroupRepositoryInterface from '@/services/WebsiteGroup/interfaces';
import { Dictionary } from 'vue-router/types/router';
import PageHeader from '@/components/layout/PageHeader.vue';
import StatusChip from '@/components/layout/StatusChip.vue';
import {
  LIST_FOOTER_PROPS,
  buildListPaginationQuery,
  defaultListOptions,
  effectivePageSize,
  listPaginationQueryEquals,
  parseListQuery,
} from '@/utils/listPagination';

@Component({
  components: {
    PageHeader,
    StatusChip,
  },
  inject: [
    'websiteMigrationsRepository',
    'websiteRepository',
    'websiteGroupRepository',
    'serverRepository',
  ],
})
export default class GroupsMigrationsList extends Vue {
  public loading = true;

  public options = defaultListOptions();

  public footerProps = LIST_FOOTER_PROPS;

  public totalMigrationsCount = 0;

  public migrationSearch = '';

  public migrations: WebsiteMigration[] = [];
  public websitesDomainMap: { [key: number]: string } = {};

  private ignoreRouteSync = false;
  private applyingRouteQuery = false;

  public headers: DataTableHeader[] = [
    { text: 'ID', value: 'id', width: '80px' },
    { text: 'Source', value: 'sourceWebsiteId' },
    { text: 'Target', value: 'targetWebsiteId' },
    { text: 'Source Server', value: 'sourceServer' },
    { text: 'Target Server', value: 'targetServer' },
    { text: 'Status', value: 'status' },
  ];

  public websiteMigrationsRepository!: WebsiteMigrationsRepositoryInterface;
  public websiteRepository!: WebsiteRepositoryInterface;
  public serverRepository!: ServerRepositoryInterface;
  public websiteGroupRepository!: WebsiteGroupRepositoryInterface;

  public websitesServersMap: { [key: number]: string } = {};
  public serversHostnameMap: { [key: string]: string } = {};

  public async created(): Promise<void> {
    this.applyRouteQuery();
    await this.loadMigrations();
  }

  public applyRouteQuery(): void {
    this.applyingRouteQuery = true;
    try {
      this.options = parseListQuery(this.$route.query);
      this.$nextTick(() => {
        this.applyingRouteQuery = false;
      });
    } catch (err) {
      this.applyingRouteQuery = false;
      throw err;
    }
  }

  public async syncRouteQuery(): Promise<void> {
    if (this.ignoreRouteSync || this.applyingRouteQuery) {
      return;
    }

    const query = buildListPaginationQuery(this.options);
    if (listPaginationQueryEquals(this.$route.query, this.options)) {
      return;
    }

    this.ignoreRouteSync = true;
    try {
      await this.$router.replace({ name: 'groups_migrations_list', query });
    } catch (err) {
      // Ignore NavigationDuplicated.
    } finally {
      this.$nextTick(() => {
        this.ignoreRouteSync = false;
      });
    }
  }

  public async onPageChange(): Promise<void> {
    await this.syncRouteQuery();
    await this.loadMigrations();
  }

  public async onItemsPerPageChange(): Promise<void> {
    this.options.page = 1;
    await this.syncRouteQuery();
    await this.loadMigrations();
  }

  @Watch('$route')
  public async onRouteChange(): Promise<void> {
    if (this.ignoreRouteSync) {
      return;
    }
    if (this.$route.name !== 'groups_migrations_list') {
      return;
    }
    if (listPaginationQueryEquals(this.$route.query, this.options)) {
      return;
    }

    this.applyRouteQuery();
    await this.loadMigrations();
  }

  public async loadMigrations(): Promise<void> {
    this.loading = true;

    try {
      const search = this.migrationSearch.toLowerCase();

      let status = '';
      if (['done', 'pending', 'error', 'failed'].includes(search)) {
        status = search;
      }

      const response = await this.websiteMigrationsRepository.getAllMigrations(
        this.options.page,
        effectivePageSize(this.options.itemsPerPage),
        undefined,
        undefined,
        'migrate-main-to-main',
        status,
      );

      if (!response.totalItems) {
        this.totalMigrationsCount = 0;
        this.migrations = [];
        return;
      }

      const sourceWebsitesIds: number[] = [];
      const targetWebsitesIds: number[] = [];
      response.migrations.forEach((m) => {
        if (!sourceWebsitesIds.includes(m.sourceWebsiteId)) {
          sourceWebsitesIds.push(m.sourceWebsiteId);
        }

        if (!targetWebsitesIds.includes(m.targetWebsiteId)) {
          targetWebsitesIds.push(m.targetWebsiteId);
        }
      });

      const websites = await Promise.all([
        ...(await this.websiteRepository.getWebsitesByIds(sourceWebsitesIds)),
        ...(await this.websiteRepository.getWebsitesByIds(targetWebsitesIds)),
      ]);

      const serversIds: string[] = [];
      websites.forEach((website) => {
        this.websitesDomainMap[website.id] = website.domain;
        this.websitesServersMap[website.id] = website.originServer;

        if (!serversIds.includes(website.originServer)) {
          serversIds.push(website.originServer);
        }
      });

      const servers = await this.serverRepository.getServersByIds(serversIds);
      servers.forEach((server) => {
        if (!this.serversHostnameMap[server._id]) {
          this.serversHostnameMap[server._id] = server.hostname;
        }
      });

      this.totalMigrationsCount = response.totalItems;
      this.migrations = response.migrations;
    } catch (error) {
      this.$errorDialog.show(error.response?.data?.error || error.message);
    } finally {
      this.loading = false;
    }
  }

  public async viewMigration(migration: WebsiteMigration): Promise<void> {
    try {
      const sourceGroup = await this.websiteGroupRepository.getGroupByWebsite(migration.sourceWebsiteId);
      const targetGroup = await this.websiteGroupRepository.getGroupByWebsite(migration.targetWebsiteId);

      if (!sourceGroup && !targetGroup) {
        throw new Error('Group Migration not found');
      }

      const query: Dictionary<string> = {};
      if (sourceGroup) {
        query.sourceGroupId = sourceGroup.id.toString();
      }

      if (targetGroup) {
        query.targetGroupId = targetGroup.id.toString();
      }

      await this.$router.push({
        name: 'group_migrations_list',
        params: { id: sourceGroup?.id || targetGroup.id },
        query,
      });
    } catch (err) {
      this.$errorDialog.show(err.response?.data?.error || err.message);
    }
  }
}
